/**
 * Header strip (SPEC §6.1): wordmark, key select (12 tonics × major/minor),
 * tempo (number input + drag-to-change), swing 0..60, style select, the
 * controller's Download-MIDI button, a "New session" button and the WebMCP
 * pill ("● WebMCP · N tools" / grey "unavailable" hint) that follows the local
 * tool registry and the native `toolchange` event.
 *
 * Every control applies a `by: 'human'` mutation and logs a human action.
 */
import type { Store } from '../state/store-api';
import type { Controller } from '../app/controller';
import type { Mode, Session, StyleId, Tonic } from '../types';
import { MODES, STYLES, SWING_MAX, SWING_MIN, TEMPO_MAX, TEMPO_MIN, TONICS, clamp } from '../types';
import { getModelContext, hasWebMCP, webmcpSource } from '../webmcp/context';
import { registry } from '../webmcp/register';
import { h, injectStyle, setFlag } from './dom';

/** Dependencies for `mountHeader()`. */
export interface HeaderDeps {
  store: Store;
  controller: Controller;
}

/** Mounted header: root element + dispose. */
export interface HeaderHandle {
  el: HTMLElement;
  dispose(): void;
}

/** Pixels of vertical drag per BPM when dragging the tempo handle. */
export const TEMPO_DRAG_PX_PER_BPM = 3;

const STYLE_LABEL: Record<StyleId, string> = { lofi: 'Lo-fi', house: 'House', synthwave: 'Synthwave' };

const CSS = `
.bm-header{position:sticky;top:0;z-index:20;background:color-mix(in srgb,var(--bg) 88%,transparent);backdrop-filter:blur(10px);
  border-bottom:1px solid var(--border)}
.bm-header__inner{max-width:var(--content-max);margin:0 auto;padding:.6rem var(--space-3);display:flex;align-items:center;gap:.6rem;flex-wrap:wrap;min-width:0}
@media (min-width:720px){.bm-header__inner{padding:.6rem var(--space-5)}}
.bm-wordmark{display:inline-flex;align-items:center;gap:.45rem;font-weight:700;letter-spacing:-.01em;font-size:1.1rem;margin:0;color:var(--fg)}
.bm-wordmark__dots{display:inline-flex;gap:2px}
.bm-wordmark__dots i{width:.55rem;height:.55rem;border-radius:50%;background:var(--human)}
.bm-wordmark__dots i+i{background:var(--agent);margin-left:-3px}
.bm-header__controls{display:flex;align-items:flex-end;gap:.5rem;flex-wrap:wrap;min-width:0}
.bm-header__tempo{display:flex;align-items:center;gap:.25rem}
.bm-tempo-drag{cursor:ns-resize;touch-action:none;user-select:none;-webkit-user-select:none;display:inline-flex;align-items:center;justify-content:center;
  width:1.4rem;min-height:2.25rem;border:1px solid var(--border-strong);border-radius:var(--radius-m);background:var(--surface-2);color:var(--fg-muted)}
.bm-tempo-drag:hover{color:var(--fg)}
.bm-tempo-drag[data-dragging]{background:var(--accent-soft);border-color:var(--accent);color:var(--fg)}
.bm-header__swing{display:inline-flex;align-items:center;gap:.4rem;min-height:2.25rem}
.bm-header__swing input{width:5.5rem}
.bm-header__swing output{font-variant-numeric:tabular-nums;min-width:1.6em;font-size:.85rem;color:var(--fg-muted)}
.bm-header__right{margin-left:auto;display:flex;align-items:center;gap:.5rem;flex-wrap:wrap;min-width:0}
@media (max-width:600px){.bm-header__right{margin-left:0;width:100%}}
`;

function keyValue(s: Session): string {
  return `${s.key.tonic}:${s.key.mode}`;
}

/** Mount the header into `container`. */
export function mountHeader(container: HTMLElement, deps: HeaderDeps): HeaderHandle {
  injectStyle('bm-header-css', CSS);
  const { store, controller } = deps;
  const disposers: Array<() => void> = [];

  // ---------- key ----------
  const keySelect = h('select', { class: 'bm-select', 'data-key': true, 'aria-label': 'Key' }) as HTMLSelectElement;
  for (const mode of MODES) {
    const group = h('optgroup', { label: mode === 'major' ? 'Major' : 'Minor' });
    for (const tonic of TONICS) group.append(h('option', { value: `${tonic}:${mode}` }, `${tonic} ${mode}`));
    keySelect.append(group);
  }
  keySelect.addEventListener('change', () => {
    const [tonic, mode] = keySelect.value.split(':') as [Tonic, Mode];
    const s = store.get();
    if (s.key.tonic === tonic && s.key.mode === mode) return;
    store.apply({ type: 'setKey', tonic, mode, transposeContent: true }, { by: 'human', label: `set key ${tonic} ${mode}` });
    store.logHuman({ kind: 'set_key', detail: `set key ${tonic} ${mode}` });
  });

  // ---------- tempo ----------
  const tempoInput = h('input', {
    class: 'bm-input',
    type: 'number',
    min: TEMPO_MIN,
    max: TEMPO_MAX,
    step: 1,
    inputmode: 'numeric',
    'data-tempo': true,
    'aria-label': 'Tempo (BPM)',
  }) as HTMLInputElement;
  const applyTempo = (raw: number) => {
    const tempo = clamp(Math.round(Number.isFinite(raw) ? raw : store.get().tempo), TEMPO_MIN, TEMPO_MAX);
    tempoInput.value = String(tempo);
    if (tempo === store.get().tempo) return;
    store.apply({ type: 'setGroove', tempo }, { by: 'human', label: `set tempo ${tempo}` });
    store.logHuman({ kind: 'set_tempo', detail: `set tempo ${tempo}` });
  };
  tempoInput.addEventListener('change', () => applyTempo(Number(tempoInput.value)));

  const dragHandle = h(
    'span',
    { class: 'bm-tempo-drag', 'data-tempo-drag': true, role: 'presentation', title: 'Drag up/down to change tempo', 'aria-hidden': 'true' },
    '⇅',
  );
  let drag: { startY: number; startTempo: number; pointerId: number | null } | null = null;
  const onDragMove = (e: PointerEvent) => {
    if (!drag) return;
    const delta = Math.round((drag.startY - e.clientY) / TEMPO_DRAG_PX_PER_BPM);
    tempoInput.value = String(clamp(drag.startTempo + delta, TEMPO_MIN, TEMPO_MAX));
  };
  const onDragEnd = () => {
    if (!drag) return;
    drag = null;
    setFlag(dragHandle, 'data-dragging', false);
    document.removeEventListener('pointermove', onDragMove);
    document.removeEventListener('pointerup', onDragEnd);
    document.removeEventListener('pointercancel', onDragEnd);
    applyTempo(Number(tempoInput.value));
  };
  dragHandle.addEventListener('pointerdown', (e) => {
    drag = { startY: e.clientY, startTempo: store.get().tempo, pointerId: e.pointerId ?? null };
    setFlag(dragHandle, 'data-dragging', true);
    try {
      if (drag.pointerId != null) dragHandle.setPointerCapture?.(drag.pointerId);
    } catch {
      /* jsdom / synthetic events */
    }
    document.addEventListener('pointermove', onDragMove);
    document.addEventListener('pointerup', onDragEnd);
    document.addEventListener('pointercancel', onDragEnd);
    e.preventDefault();
  });
  disposers.push(onDragEnd);

  // ---------- swing ----------
  const swingOut = h('output', { 'aria-hidden': 'true' }) as HTMLOutputElement;
  const swingInput = h('input', {
    class: 'bm-range',
    type: 'range',
    min: SWING_MIN,
    max: SWING_MAX,
    step: 1,
    'data-swing': true,
    'aria-label': 'Swing',
  }) as HTMLInputElement;
  swingInput.addEventListener('input', () => {
    swingOut.value = swingInput.value;
  });
  swingInput.addEventListener('change', () => {
    const swing = clamp(Math.round(Number(swingInput.value)), SWING_MIN, SWING_MAX);
    swingInput.value = String(swing);
    swingOut.value = String(swing);
    if (swing === store.get().swing) return;
    store.apply({ type: 'setGroove', swing }, { by: 'human', label: `set swing ${swing}` });
    store.logHuman({ kind: 'set_swing', detail: `set swing ${swing}` });
  });

  // ---------- style ----------
  const styleSelect = h(
    'select',
    { class: 'bm-select', 'data-style': true, 'aria-label': 'Style' },
    STYLES.map((s) => h('option', { value: s }, STYLE_LABEL[s])),
  ) as HTMLSelectElement;
  styleSelect.addEventListener('change', () => {
    const style = styleSelect.value as StyleId;
    if (!STYLES.includes(style) || style === store.get().style) return;
    store.apply({ type: 'setGroove', style }, { by: 'human', label: `set style ${style}` });
    store.logHuman({ kind: 'set_style', detail: `set style ${style}` });
  });

  // ---------- new session ----------
  const newBtn = h(
    'button',
    { type: 'button', class: 'bm-btn bm-btn--ghost', 'data-new': true, title: 'Start a blank session' },
    'New',
  );
  newBtn.addEventListener('click', () => void controller.newSession());

  // ---------- WebMCP pill ----------
  const pillText = h('span', { class: 'bm-pill__text' });
  const pill = h('span', { class: 'bm-pill', 'data-webmcp': true, role: 'status' }, h('span', { class: 'bm-pill__dot' }), pillText);
  const renderPill = () => {
    const on = hasWebMCP();
    const n = registry.count();
    pill.classList.toggle('bm-pill--on', on);
    pillText.textContent = on
      ? `WebMCP · ${n} tool${n === 1 ? '' : 's'}`
      : 'WebMCP unavailable — try Chrome 149+ with the flag, or ChatGPT desktop';
    pill.title = on
      ? `${n} tool${n === 1 ? '' : 's'} registered on ${webmcpSource()}.modelContext`
      : `${n} tool${n === 1 ? '' : 's'} registered locally; no document.modelContext in this browser`;
  };
  disposers.push(registry.subscribe(renderPill));
  const mc = getModelContext();
  if (mc?.addEventListener) {
    mc.addEventListener('toolchange', renderPill);
    disposers.push(() => mc.removeEventListener?.('toolchange', renderPill));
  }
  renderPill();

  // ---------- assemble ----------
  const field = (label: string, control: HTMLElement) => h('label', { class: 'bm-field' }, h('span', null, label), control);
  const el = h(
    'header',
    { class: 'bm-header' },
    h(
      'div',
      { class: 'bm-header__inner' },
      h('h1', { class: 'bm-wordmark' }, h('span', { class: 'bm-wordmark__dots', 'aria-hidden': 'true' }, h('i'), h('i')), 'Bandmate'),
      h(
        'div',
        { class: 'bm-header__controls' },
        field('Key', keySelect),
        h('div', { class: 'bm-field' }, h('span', null, 'Tempo'), h('div', { class: 'bm-header__tempo' }, tempoInput, dragHandle)),
        h('div', { class: 'bm-field' }, h('span', null, 'Swing'), h('div', { class: 'bm-header__swing' }, swingInput, swingOut)),
        field('Style', styleSelect),
      ),
      h('div', { class: 'bm-header__right' }, newBtn, controller.downloadButton(), pill),
    ),
  );

  const sync = (s: Session) => {
    if (keySelect.value !== keyValue(s)) keySelect.value = keyValue(s);
    if (!drag && document.activeElement !== tempoInput) tempoInput.value = String(s.tempo);
    if (document.activeElement !== swingInput) {
      swingInput.value = String(s.swing);
      swingOut.value = String(s.swing);
    }
    if (styleSelect.value !== s.style) styleSelect.value = s.style;
  };
  sync(store.get());
  disposers.push(store.subscribe((_s, meta) => {
    if (meta.source !== 'transient') sync(store.get());
  }));

  container.append(el);
  return {
    el,
    dispose() {
      for (const d of disposers) d();
      el.remove();
    },
  };
}
